// Tenant branding helper — fetches tenant name + logo (data URL) once per session,
// re-fetching when the tenant switcher fires 'tenant-logo-updated'.
// Used by every PDF / Excel exporter so branding is consistent across reports.

import axios from 'axios';
import { API } from '../App';

let cachedBranding = null;
let cachedForTenantId = null;

if (typeof window !== 'undefined') {
  window.addEventListener('tenant-logo-updated', () => {
    cachedBranding = null;
    cachedForTenantId = null;
  });
}

/**
 * @param {string|null} viewTenantId  – super-admin's currently-viewed tenant, if any
 * @returns {Promise<{name: string|null, logo_url: string|null}>}
 */
export async function fetchTenantBranding(viewTenantId = null) {
  if (cachedBranding && cachedForTenantId === (viewTenantId || null)) {
    return cachedBranding;
  }
  try {
    const q = viewTenantId ? `?view_tenant_id=${viewTenantId}` : '';
    const res = await axios.get(`${API}/tenant/branding${q}`);
    cachedBranding = res.data || { name: null, logo_url: null };
    cachedForTenantId = viewTenantId || null;
    return cachedBranding;
  } catch {
    return { name: null, logo_url: null };
  }
}

/**
 * Draws a compact header row on a jsPDF instance (logo left, tenant name centered, doc title right).
 * Returns the Y coordinate below the header where the caller can continue drawing.
 *
 * @param {jsPDF} doc
 * @param {{name: string|null, logo_url: string|null}} branding
 * @param {string} title             report title, e.g. "Gap Assessment Report"
 * @param {object} [opts]            { marginX = 14, top = 12, height = 20 }
 */
export function drawBrandedPdfHeader(doc, branding, title, opts = {}) {
  const marginX = opts.marginX ?? 14;
  const top = opts.top ?? 12;
  const h = opts.height ?? 20;
  const pageW = doc.internal.pageSize.getWidth();

  // Logo (left)
  if (branding?.logo_url) {
    try {
      const fmt = branding.logo_url.includes('image/jpeg') ? 'JPEG'
                : branding.logo_url.includes('image/webp') ? 'WEBP'
                : 'PNG';
      doc.addImage(branding.logo_url, fmt, marginX, top, h, h);
    } catch {/* silently skip if unsupported */}
  }

  // Tenant name (center)
  const centerX = pageW / 2;
  const nameY = top + h / 2 - 2;
  if (branding?.name) {
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(branding.name, centerX, nameY, { align: 'center' });
  }

  // Report title (right / centered below name)
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text(title, centerX, nameY + 5, { align: 'center' });

  // Divider
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.3);
  doc.line(marginX, top + h + 2, pageW - marginX, top + h + 2);

  doc.setTextColor(0, 0, 0);
  return top + h + 6;
}

/**
 * Prepends a branded header row to a 2-D array before it goes into a CSV / xlsx sheet.
 * @param {string[][]} rows   original rows (first row = headers)
 * @param {{name: string|null}} branding
 * @param {string} title
 * @returns {string[][]}
 */
export function prependBrandingRowsForSheet(rows, branding, title) {
  const stamp = new Date().toISOString().slice(0, 19).replace('T', ' ');
  const brand = branding?.name ? `${branding.name} — ${title}` : title;
  return [[brand], [`Generated at ${stamp} UTC`], [], ...rows];
}
